import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AUTH_API_BASE_URL, AUTH_SUPABASE_CLIENT } from '@pressteaze/auth';
import { firstValueFrom } from 'rxjs';
import type { ConfigurationContext } from './configuration.models';

export class ConfigurationApiError extends Error {
	constructor(readonly code: string) { super(code); }
}

@Injectable({ providedIn: 'root' })
export class ConfigurationApiService {
	private readonly http = inject(HttpClient);
	private readonly baseUrl = inject(AUTH_API_BASE_URL);
	private readonly supabase = inject(AUTH_SUPABASE_CLIENT);

	context(storeId?: string): Promise<ConfigurationContext> {
		return this.post({ action: 'context', store_id: storeId ?? null });
	}

	command<T = { ok: boolean }>(body: Record<string, unknown>): Promise<T> {
		return this.post<T>(body);
	}

	userCommand<T = { ok: boolean }>(body: Record<string, unknown>): Promise<T> {
		return this.post<T>(body, 'user-admin');
	}

	private async post<T>(body: Record<string, unknown>, endpoint = 'configuration-admin'): Promise<T> {
		const { data } = await this.supabase.auth.getSession();
		const token = data.session?.access_token;
		if (!token) throw new ConfigurationApiError('UNAUTHORIZED');
		try {
			return await firstValueFrom(this.http.post<T>(`${this.baseUrl}/${endpoint}`, body, {
				headers: new HttpHeaders({ Authorization: `Bearer ${token}` }),
			}));
		} catch (error) {
			if (error instanceof HttpErrorResponse)
				throw new ConfigurationApiError(typeof error.error?.code === 'string' ? error.error.code : 'REQUEST_FAILED');
			throw error;
		}
	}
}
